/**
 * React Theme Provider
 * Provides theme values to React components via Context API
 * 
 * @typedef {import('../types/theme').ThemeConfig} ThemeConfig
 * @typedef {import('../types/theme').ThemeVars} ThemeVars
 * @typedef {import('../types/theme').ThemeContextValue} ThemeContextValue
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { THEME, THEME_VARS, applyThemeVariables, initializeTheme } from "../config/theme";
import { updateMuiTheme } from "../config/muiTheme";

const ThemeContext = createContext({
  theme: THEME,
  themeVars: THEME_VARS,
  muiTheme: null,
  updateTheme: () => {},
});

/**
 * ThemeProvider component
 * Wraps the application to provide theme values to all child components
 * Automatically loads saved theme from localStorage if available
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {ThemeConfig} [props.initialTheme=THEME] - Initial theme configuration (defaults to THEME)
 * @returns {React.ReactElement} ThemeProvider component
 */
export const ThemeProvider = ({ children, initialTheme = THEME }) => {
  // Initialize with saved theme from localStorage or default theme
  const [theme, setTheme] = useState(() => {
    // Try to load from localStorage on initial mount
    const savedTheme = typeof window !== 'undefined' && window.localStorage 
      ? (() => {
          try {
            const stored = localStorage.getItem('admin_theme');
            return stored ? JSON.parse(stored) : null;
          } catch {
            return null;
          }
        })()
      : null;
    
    return savedTheme || initialTheme;
  });
  
  // Track MUI theme separately to trigger re-renders
  const [muiTheme, setMuiTheme] = useState(() => updateMuiTheme(theme));
  
  useEffect(() => {
    // Apply CSS variables theme on mount and when theme changes
    applyThemeVariables(theme);
    
    // Update MUI theme when theme changes
    const newMuiTheme = updateMuiTheme(theme);
    setMuiTheme(newMuiTheme);
    
    // Store as current theme for partial updates
    window.__CURRENT_THEME__ = theme;
    
    // Register global callback for runtime updates
    window.__THEME_UPDATE_CALLBACK__ = setTheme;
    
    return () => {
      delete window.__THEME_UPDATE_CALLBACK__;
    };
  }, [theme]);
  
  const updateTheme = (newTheme) => {
    setTheme(newTheme);
  };
  
  return (
    <ThemeContext.Provider value={{ theme, themeVars: THEME_VARS, muiTheme, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme hook
 * Returns the current theme context
 * 
 * @returns {ThemeContextValue} Theme context with theme, themeVars, and updateTheme
 * @throws {Error} If used outside ThemeProvider
 * 
 * @example
 * const { theme, themeVars, updateTheme } = useTheme();
 * console.log(theme.colors.primary); // "#249B47"
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

/**
 * useThemeColor hook
 * Returns a specific color value from the theme
 * 
 * @param {string} colorKey - The key of the color in theme.colors
 * @returns {string|undefined} The color value or undefined if not found
 * 
 * @example
 * const primaryColor = useThemeColor('primary');
 * console.log(primaryColor); // "#249B47"
 */
export const useThemeColor = (colorKey) => {
  const { theme } = useTheme();
  const color = theme.colors[colorKey];
  
  // Log warning for undefined theme tokens
  if (color === undefined) {
    console.warn(`Theme color not found: ${colorKey}`);
  }
  
  return color;
};

/**
 * useThemeVar hook
 * Returns a theme value by resolving a nested path
 * 
 * @param {string} path - Dot-separated path to the theme value (e.g., "colors.primary", "typography.fontFamily")
 * @returns {any} The resolved value or undefined if path doesn't exist
 * 
 * @example
 * const primaryVar = useThemeVar('colors.primary');
 * const fontFamily = useThemeVar('typography.fontFamily');
 */
export const useThemeVar = (path) => {
  const { themeVars } = useTheme();
  const keys = path.split(".");
  let value = themeVars;
  
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      break;
    }
  }
  
  return value;
};
